/* eslint-disable no-param-reassign */
import produce from 'immer';
import type { CaseBannerType } from 'types';
import {
  DUPLICATE_CASE_OPEN,
  DUPLICATE_CASE_CLOSE,
  OPEN_CASE_SEARCH,
  CLOSE_CASE_SEARCH,
  CLEAR_CASE,
  GET_CASE_BANNER_INFORMATION,
  SET_CASE_JURISDICTION_ASSUMED,
  SET_CASE_ID,
  OPEN_RECENT_CASES,
  CLOSE_RECENT_CASES,
} from 'src/actions/caseActions';

type CaseId = string | number | null | undefined;

type CaseItemState = {
  id: CaseId;
  donorHeightUnit: string;
  donorWeightUnit: string;
  jurisdictionAssumed?: boolean;
  [key: string]: unknown;
};

type DuplicateCaseState = {
  warningOpen: boolean;
  duplicateCaseItem: unknown;
  override: boolean;
};

type CaseContactsState = {
  kins: Array<Record<string, unknown>>;
};

type BannerState = CaseBannerType;

export type CaseState = {
  caseItem: CaseItemState;
  banner: BannerState;
  caseStatusList: unknown[];
  familyStatusList: unknown[];
  responsibleFollowUpList: unknown[];
  casePartners: unknown;
  outcomeList: unknown[];
  outcomeTypeList: unknown[];
  outcomeDetailList: unknown[];
  nextStepsList: unknown[];
  titleList: unknown[];
  caseSearchOpen: boolean;
  duplicateCase: DuplicateCaseState;
  caseContacts: CaseContactsState;
  contactsKins: unknown[];
  jurisdictionAssumed: boolean;
  recentCasesOpen: boolean;
  recentCasesAnchorEl: unknown;
};

type CaseAction =
  | {
      type: typeof DUPLICATE_CASE_OPEN;
      payload: { duplicateCase: unknown };
    }
  | {
      type: typeof DUPLICATE_CASE_CLOSE;
    }
  | {
      type: typeof SET_CASE_ID;
      payload: CaseId;
    }
  | {
      type: typeof GET_CASE_BANNER_INFORMATION;
      payload: BannerState | null | undefined;
    }
  | {
      type: typeof CLEAR_CASE;
    }
  | {
      type: typeof OPEN_CASE_SEARCH;
    }
  | {
      type: typeof CLOSE_CASE_SEARCH;
    }
  | {
      type: typeof SET_CASE_JURISDICTION_ASSUMED;
      payload: { jurisdictionAssumed: boolean };
    }
  | {
      type: typeof OPEN_RECENT_CASES;
    }
  | {
      type: typeof CLOSE_RECENT_CASES;
    };

const initialBanner: CaseBannerType = {
  evaluationReceived: false,
  referralAuthorized: false,
  id: 0,
  locked: false,
  donorIdTissue: null,
  jurisdictionAssumed: false,
  donor: {
    entityDonorId: '',
    age: null,
    registered: null,
    id: 0,
    firstName: '',
    lastName: '',
    weight: 0,
    height: null,
    weightUnit: 'kg',
    heightUnit: 'cm',
    deathDate: null,
    deadOnArrival: false,
    deathDatePending: false,
    mrn: '',
    admissionDate: null,
    admissionDateTimezone: null,
    din: '',
    birthDate: '',
    gender: '',
    genderId: null,
    deathDateTimezone: null,
  },
  organization: {
    id: 0,
    name: '',
    serviceAlerts: [],
  },
  partners: {},
  hasReactiveSerology: false,
} as CaseBannerType;

const initialState: CaseState = {
  caseItem: {
    id: null,
    callerLastName: '',
    callerFirstName: '',
    donorFirstName: '',
    donorLastName: '',
    callerPhone: null,
    dateNotified: null,
    statlinePhone: null,
    referringComment: '',
    antibiotics: [{ typeId: true, doses: 2, description: 'send' }],
    culture: [{}],
    cbc: [{}],
    temperature: [{}],
    lactate: [{}],
    antifungals: null,
    assignmentsReferrer: {
      id: '',
      status: '',
    },
    assignmentsReviewer: {
      id: '',
      status: '',
    },
    assignmentsEyeRecoverer: {
      id: '',
      status: '',
    },
    assignmentsHospitalDevelopment: {
      id: '',
      status: '',
    },
    assignmentsTransporter: {
      id: '',
      status: '',
    },
    donorHeightUnit: 'cm',
    donorWeightUnit: 'kg',
  },
  banner: initialBanner,
  caseStatusList: [],
  familyStatusList: [],
  responsibleFollowUpList: [],
  casePartners: null,
  outcomeList: [],
  outcomeTypeList: [],
  outcomeDetailList: [],
  nextStepsList: [],
  titleList: [],
  caseSearchOpen: false,
  duplicateCase: {
    warningOpen: false,
    duplicateCaseItem: {},
    override: false,
  },
  caseContacts: {
    kins: [{}],
  },
  contactsKins: [],
  jurisdictionAssumed: false,
  recentCasesOpen: false,
  recentCasesAnchorEl: null,
};

const caseReducer = (
  state: CaseState = initialState,
  action: CaseAction,
): CaseState => {
  switch (action.type) {
    case DUPLICATE_CASE_OPEN: {
      const { duplicateCase } = action.payload;

      return produce(state, (draft) => {
        draft.duplicateCase = {
          duplicateCaseItem: duplicateCase,
          warningOpen: true,
          override: draft.duplicateCase.override,
        };
      });
    }

    case DUPLICATE_CASE_CLOSE: {
      return produce(state, (draft) => {
        draft.duplicateCase.warningOpen =
          initialState.duplicateCase.warningOpen;
        draft.duplicateCase.duplicateCaseItem =
          initialState.duplicateCase.duplicateCaseItem;
      });
    }

    case SET_CASE_ID: {
      const caseId = action.payload;

      return produce(state, (draft) => {
        draft.caseItem.id = caseId;
      });
    }

    case GET_CASE_BANNER_INFORMATION: {
      const record = action.payload;
      return produce(state, (draft) => {
        draft.banner = record ?? initialState.banner;
      });
    }

    case CLEAR_CASE: {
      return produce(state, (draft) => {
        draft.caseItem = initialState.caseItem;
        draft.banner = initialState.banner;
      });
    }

    case OPEN_CASE_SEARCH: {
      return produce(state, (draft) => {
        draft.caseSearchOpen = true;
      });
    }

    case CLOSE_CASE_SEARCH: {
      return produce(state, (draft) => {
        draft.caseSearchOpen = false;
      });
    }

    case SET_CASE_JURISDICTION_ASSUMED: {
      const { jurisdictionAssumed } = action.payload;
      return produce(state, (draft) => {
        draft.caseItem.jurisdictionAssumed = jurisdictionAssumed;
      });
    }

    case OPEN_RECENT_CASES: {
      return produce(state, (draft) => {
        draft.recentCasesOpen = true;
      });
    }

    case CLOSE_RECENT_CASES: {
      return produce(state, (draft) => {
        draft.recentCasesOpen = false;
      });
    }

    default: {
      return state;
    }
  }
};

export default caseReducer;
